import React from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { GeoAlt } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { validateUSState, validateUSStateExcludingAkHi } from '../utils/addressUtils';
import './AddressModal.scss';

interface AddressModalProps {
  show: boolean;
  onHide: () => void;
  selectedAddressOption: string;
  temporaryAddress: any;
  recommendedShippingAddress: any;
  addressValidationResult: any;
  formShippingAddress: any;
  onAddressOptionChange: (e: React.ChangeEvent<HTMLInputElement>, option: string) => void;
  onShippingAddressUpdate: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirmAddress: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // New props for dual address support
  isBilling?: boolean;
  temporaryBillingAddress?: any;
  recommendedBillingAddress?: any;
  selectedBillingAddressOption?: string;
  formBillingAddress?: any;
  onBillingAddressOptionChange?: (e: React.ChangeEvent<HTMLInputElement>, option: string) => void;
  billingValidationResult?: any;
  shippingValidationResult?: any;
  // Props for highlighting differences
  billingAddressDifferences?: string[];
  shippingAddressDifferences?: string[];
  // Brand for MSI-specific validation
  brand?: string;
  // Validation errors
  validationErrors?: any;
}

const AddressModal: React.FC<AddressModalProps> = ({
  show,
  onHide,
  selectedAddressOption,
  temporaryAddress,
  recommendedShippingAddress,
  addressValidationResult,
  formShippingAddress,
  onAddressOptionChange,
  onShippingAddressUpdate,
  onConfirmAddress,
  // Dual address props
  isBilling = false,
  temporaryBillingAddress,
  recommendedBillingAddress,
  selectedBillingAddressOption = "yourAddress",
  formBillingAddress,
  onBillingAddressOptionChange,
  billingValidationResult,
  shippingValidationResult,
  // Differences for highlighting
  billingAddressDifferences = [],
  shippingAddressDifferences = [],
  // Brand and validation errors
  brand,
  validationErrors
}) => {
  const { t } = useTranslation();
  const { locale } = useLocalization();

  // Helper function to check if a field is different (memoized)
  const isDifferent = React.useCallback((fieldName: string, differences: string[]) => {
    return differences.includes(fieldName);
  }, []);

  const normalize = React.useCallback((value: string) => {
    return value?.toString().trim().toUpperCase();
  }, []);

  // Helper function to check if address has valid city, state, and postal code
  const isValidCityStatePostalStrict = React.useCallback((validationResult: any, userInput: any) => {
    if (!validationResult?.result) {
      return false;
    }

    // Only run this validation for US addresses
    const country = userInput?.country || userInput?.countryValue;
    if (country !== 'US' && country !== 'USA' && country !== 'United States') {
      return true; // Skip validation for non-US addresses
    }

    const verdict = validationResult.result.verdict || {};
    const address = validationResult.result.address || {};
    const components = address.addressComponents || [];

    // Use possibleNextAction for validation decision
    // ACCEPT: Address is fully validated and can be used as-is
    // CONFIRM: Minor issues but plausible (e.g., UNCONFIRMED_BUT_PLAUSIBLE)
    // CONFIRM_ADD_SUBPREMISES: Missing unit/apt number (but otherwise valid)
    // FIX: Address has serious issues and must be corrected
    const possibleNextAction = verdict.possibleNextAction;

    // Allow ACCEPT, CONFIRM, and CONFIRM_ADD_SUBPREMISES
    // Only block FIX (serious validation issues)
    if (possibleNextAction === "FIX") {
      return false;
    }

    // If possibleNextAction is ACCEPT, CONFIRM, or CONFIRM_ADD_SUBPREMISES, proceed with additional checks

    // Build component map
    const componentMap = new Map(
      components.map((c: any) => [c.componentType, c])
    );

    // 2. Required components (city, state, postal)
    const requiredComponents = [
      "locality",
      "administrative_area_level_1",
      "postal_code"
    ];

    // 3. Missing required components
    for (const type of requiredComponents) {
      if (!componentMap.has(type)) {
        return false;
      }
    }

    // 4. Unconfirmed required components (allow CONFIRMED and UNCONFIRMED_BUT_PLAUSIBLE)
    for (const type of requiredComponents) {
      const component = componentMap.get(type) as any;
      const confirmationLevel = component?.confirmationLevel;
      if (confirmationLevel !== "CONFIRMED" && confirmationLevel !== "UNCONFIRMED_BUT_PLAUSIBLE") {
        return false;
      }
    }

    // 5. BLOCK inferred-but-wrong STATE (critical fix)
    const stateComponent = componentMap.get("administrative_area_level_1") as any;
    const googleState =
      stateComponent?.componentName?.text;

    if (
      userInput?.stateValue &&
      googleState &&
      normalize(userInput.stateValue) !== normalize(googleState)
    ) {
      return false;
    }

    // 6. Auto-correction handling (allow suffix-only postal correction)
    if (verdict.hasReplacedComponents) {
      const replacedComponents = components
        .filter((c: any) => c.replaced)
        .map((c: any) => c.componentType);

      const postalComponent = componentMap.get("postal_code") as any;
      const isPostalSuffixOnly =
        replacedComponents.length === 1 &&
        replacedComponents[0] === "postal_code" &&
        typeof userInput?.postalCode === "string" &&
        postalComponent?.componentName?.text?.startsWith(userInput.postalCode);

      if (!isPostalSuffixOnly) {
        return false;
      }
    }

    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to check if validation result has truly unconfirmed components
  // (excludes UNCONFIRMED_BUT_PLAUSIBLE which should be allowed)
  const hasUnconfirmedComponents = React.useCallback((validationResult: any) => {
    const components = validationResult?.result?.address?.addressComponents || [];
    
    // Check if any component has a confirmation level that is truly problematic
    // Allow: CONFIRMED and UNCONFIRMED_BUT_PLAUSIBLE
    // Block: UNCONFIRMED (null/undefined or explicitly UNCONFIRMED)
    const hasTrulyUnconfirmed = components.some((component: any) => {
      const level = component.confirmationLevel;
      const type = component.componentType;
      
      // Ignore administrative_area_level_3, 4, 5 (these are often missing)
      if (
        type === "administrative_area_level_3" ||
        type === "administrative_area_level_4" ||
        type === "administrative_area_level_5"
      ) {
        return false;
      }
      
      // Block if not CONFIRMED and not UNCONFIRMED_BUT_PLAUSIBLE
      return level !== "CONFIRMED" && level !== "UNCONFIRMED_BUT_PLAUSIBLE";
    });
    
    return hasTrulyUnconfirmed;
  }, []);

  // Consolidated validation check for a single address
  const getAddressValidationErrors = React.useCallback((
    validationResult: any,
    userInput: any,
    addressType: string,
    includeStrictValidation: boolean = true
  ) => {
    const errors: { type: string; message: string; components?: string[] }[] = [];

    // Check for truly unconfirmed components (not UNCONFIRMED_BUT_PLAUSIBLE)
    if (hasUnconfirmedComponents(validationResult)) {
      const components = validationResult?.result?.address?.addressComponents || [];
      const unconfirmedTypes = components
        .filter((component: any) => {
          const level = component.confirmationLevel;
          const type = component.componentType;
          
          // Ignore administrative_area_level_3, 4, 5
          if (
            type === "administrative_area_level_3" ||
            type === "administrative_area_level_4" ||
            type === "administrative_area_level_5"
          ) {
            return false;
          }
          
          // Only include if not CONFIRMED and not UNCONFIRMED_BUT_PLAUSIBLE
          return level !== "CONFIRMED" && level !== "UNCONFIRMED_BUT_PLAUSIBLE";
        })
        .map((component: any) => component.componentType);

      if (unconfirmedTypes.length > 0) {
        errors.push({
          type: 'missing',
          message: t("MISSING"),
          components: unconfirmedTypes
        });
      }
    }

    // Check for city/state/postal validation (only if requested)
    if (includeStrictValidation && !isValidCityStatePostalStrict(validationResult, userInput)) {
      errors.push({
        type: 'validation',
        message: t("CITY_STATE_ZIP_MISMATCH")
      });
    }

    return errors;
  }, [hasUnconfirmedComponents, isValidCityStatePostalStrict, t]);

  // Check if strict validation failed (but may have recommended address)
  const hasStrictValidationFailure = React.useMemo(() => {
    const billingStrictFailed = !isValidCityStatePostalStrict(billingValidationResult, formBillingAddress);
    const shippingStrictFailed = isBilling && !isValidCityStatePostalStrict(shippingValidationResult, formShippingAddress);
    return billingStrictFailed || shippingStrictFailed;
  }, [isBilling, billingValidationResult, shippingValidationResult, formBillingAddress, formShippingAddress, isValidCityStatePostalStrict]);

  // Check if user can confirm (strict validation must pass OR recommended address must be selected)
  // Only hide confirm button for strict validation errors, not for missing components
  const canConfirmAddress = React.useMemo(() => {
    // For all brands, validate shipping and billing address states before allowing confirmation
    if (brand) {
      const isAkHiRestrictedPartner = ['MSI', 'IBUYPOWER'].includes((brand || '').trim().toUpperCase());

      // Validate shipping address (use recommended if selected, otherwise use form address)
      const shippingAddressToValidate = (selectedAddressOption === "recommendedAddress" && recommendedShippingAddress)
        ? recommendedShippingAddress
        : formShippingAddress;
      
      const shippingStateValidation = isAkHiRestrictedPartner
        ? validateUSStateExcludingAkHi(shippingAddressToValidate)
        : validateUSState(shippingAddressToValidate);
      
      if (!shippingStateValidation.isValid) {
        return false; // Block confirmation if shipping state is not allowed
      }

      // Also validate billing address if different
      if (isBilling && formBillingAddress) {
        const billingAddressToValidate = (selectedBillingAddressOption === "recommendedAddress" && recommendedBillingAddress)
          ? recommendedBillingAddress
          : formBillingAddress;
        
        const billingStateValidation = isAkHiRestrictedPartner
          ? validateUSStateExcludingAkHi(billingAddressToValidate)
          : validateUSState(billingAddressToValidate);
        
        if (!billingStateValidation.isValid) {
          return false; // Block confirmation if billing state is not allowed
        }
      }
    }

    // If strict validation passed for all addresses, can confirm
    if (!hasStrictValidationFailure) {
      return true;
    }

    // Strict validation failed - check if recommended addresses are selected
    if (isBilling) {
      const billingStrictFailed = !isValidCityStatePostalStrict(billingValidationResult, formBillingAddress);
      const shippingStrictFailed = !isValidCityStatePostalStrict(shippingValidationResult, formShippingAddress);

      // For billing: if strict failed and there's a recommended address, must select it
      if (billingStrictFailed) {
        if (recommendedBillingAddress) {
          // If there's a recommended address, must select it
          if (selectedBillingAddressOption !== "recommendedAddress") {
            return false;
          }
        } else {
          // No recommended address available, hide confirm button
          return false;
        }
      }

      // For shipping: if strict failed and there's a recommended address, must select it
      if (shippingStrictFailed) {
        if (recommendedShippingAddress) {
          // If there's a recommended address, must select it
          if (selectedAddressOption !== "recommendedAddress") {
            return false;
          }
        } else {
          // No recommended address available, hide confirm button
          return false;
        }
      }

      return true;
    } else {
      // Single mode: billing address only (displayed as shipping)
      const billingStrictFailed = !isValidCityStatePostalStrict(billingValidationResult, formBillingAddress);
      if (billingStrictFailed) {
        if (recommendedShippingAddress) {
          // If there's a recommended address, must select it
          return selectedAddressOption === "recommendedAddress";
        } else {
          // No recommended address available, hide confirm button
          return false;
        }
      }
      return true;
    }
  }, [hasStrictValidationFailure, isBilling, billingValidationResult, shippingValidationResult, formBillingAddress, formShippingAddress, recommendedBillingAddress, recommendedShippingAddress, selectedBillingAddressOption, selectedAddressOption, isValidCityStatePostalStrict, brand]);

  // Show recommended addresses even when strict validation fails (but not when there are missing components)
  const shouldShowBillingRecommended = React.useMemo(() => {
    // For all brands, only show recommended addresses if they're from valid states
    if (brand) {
      const isAkHiRestrictedPartner = ['MSI', 'IBUYPOWER'].includes((brand || '').trim().toUpperCase());
      if (!recommendedBillingAddress) {
        return false;
      }
      const stateValidation = isAkHiRestrictedPartner
        ? validateUSStateExcludingAkHi(recommendedBillingAddress)
        : validateUSState(recommendedBillingAddress);
      return stateValidation.isValid;
    }
    // Only hide if there are truly problematic unconfirmed components AND strict validation failed
    // This allows recommended addresses to show for valid addresses that just have minor differences
    const strictFailed = !isValidCityStatePostalStrict(billingValidationResult, formBillingAddress);
    if (strictFailed && hasUnconfirmedComponents(billingValidationResult)) {
      return false;
    }
    // Show if there's a recommended address
    return !!recommendedBillingAddress;
  }, [billingValidationResult, recommendedBillingAddress, hasUnconfirmedComponents, brand, isValidCityStatePostalStrict, formBillingAddress]);

  const shouldShowShippingRecommended = React.useMemo(() => {
    // For all brands, only show recommended addresses if they're from valid states
    if (brand) {
      const isAkHiRestrictedPartner = ['MSI', 'IBUYPOWER'].includes((brand || '').trim().toUpperCase());
      if (!recommendedShippingAddress) {
        return false;
      }
      const stateValidation = isAkHiRestrictedPartner
        ? validateUSStateExcludingAkHi(recommendedShippingAddress)
        : validateUSState(recommendedShippingAddress);
      return stateValidation.isValid;
    }
    const validationResult = isBilling ? shippingValidationResult : billingValidationResult;
    const formAddress = isBilling ? formShippingAddress : formBillingAddress;
    // Only hide if there are truly problematic unconfirmed components AND strict validation failed
    // This allows recommended addresses to show for valid addresses that just have minor differences
    const strictFailed = !isValidCityStatePostalStrict(validationResult, formAddress);
    if (strictFailed && hasUnconfirmedComponents(validationResult)) {
      return false;
    }
    // Show if there's a recommended address
    return !!recommendedShippingAddress;
  }, [isBilling, shippingValidationResult, billingValidationResult, recommendedShippingAddress, hasUnconfirmedComponents, brand, isValidCityStatePostalStrict, formShippingAddress, formBillingAddress]);

  // Consolidated error rendering function
  const renderAddressErrors = React.useCallback((
    validationResult: any,
    userInput: any,
    addressType: string
  ) => {
    const errors = getAddressValidationErrors(validationResult, userInput, addressType);

    if (errors.length === 0) {
      return null;
    }

    return (
      <div className="card missingInputs" style={{ marginTop: '1rem' }}>
        <h4 style={{ color: '#d32f2f' }} aria-label={addressType ? `${addressType} - ${t("ADDRESS_VALIDATION_ERROR")}` : t("ADDRESS_VALIDATION_ERROR")}>
          {addressType ? `${addressType} - ${t("ADDRESS_VALIDATION_ERROR")}` : t("ADDRESS_VALIDATION_ERROR")}
        </h4>
        {errors.map((error, idx) => (
          <div key={idx} style={{ marginTop: idx > 0 ? '1rem' : '0.5rem' }}>
            {error.type === 'missing' && error.components && (
              <>
                <p style={{ margin: '0.5rem 0', fontWeight: 'bold' }}>{error.message}:</p>
                <ul style={{ marginLeft: '1.5rem' }}>
                  {error.components.map((componentType: string, index: number) => {
                    const translationKey =
                      componentType === "postal_code"
                        ? "ZIP_CODE"
                        : componentType === "postal_code_suffix"
                          ? "POSTAL_CODE_SUFFIX"
                          : componentType === "point_of_interest"
                            ? "POINT_OF_INTEREST"
                            : componentType === "plus_code"
                              ? "STREET_NUMBER"
                              : componentType === "subpremise"
                                ? "SUBPREMISE"
                                : componentType === "premise"
                                  ? "PREMISE"
                                  : componentType === "country"
                                    ? "COUNTRY"
                                    : componentType === "route"
                                      ? "ROUTE"
                                      : componentType === "locality"
                                        ? "LOCALITY"
                                        : componentType === "street_number"
                                          ? "STREET_NUMBER"
                                          : componentType === "administrative_area_level_1"
                                            ? "STATE"
                                            : componentType === "administrative_area_level_2"
                                              ? "CITY"
                                              : componentType;
                    return <li key={index} aria-label={t(translationKey)}>{t(translationKey)}</li>;
                  })}
                </ul>
              </>
            )}
            {error.type === 'validation' && (
              <p style={{ margin: '0.5rem 0' }}>{error.message}</p>
            )}
          </div>
        ))}
      </div>
    );
  }, [getAddressValidationErrors, t]);
  return (
    <Modal
      className={`address-modal ${isBilling ? 'dual-address' : ''}`}
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      aria-labelledby="address-modal-title"
      aria-describedby="address-modal-desc"
      size={isBilling ? "xl" : "lg"}
    >
      <Modal.Header closeButton={false}>
        <Modal.Title id="address-modal-title">
          <GeoAlt className="me-2" />
          {t('CONFIRM_ADDRESS')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="address-modal-desc">
        <div className={`address-container ${isBilling ? 'dual-columns' : ''}`}>
          
          {/* Billing Address Section */}
          {isBilling && (
            <div className="address-column billing-column">
              <h5 className="address-section-title">{t("BILLING_ADDRESS")}</h5>
              <div className="address-options">
                <fieldset aria-label={t("BILLING_ADDRESS_OPTIONS")}>
                  <legend className="visually-hidden">{t("BILLING_ADDRESS_OPTIONS")}</legend>
                  <div className="radioGroup">
                    <Form.Check
                      type="radio"
                      name="billingAddressOption"
                      id="yourBillingAddress"
                      checked={selectedBillingAddressOption === "yourAddress"}
                      onChange={(e) => onBillingAddressOptionChange?.(e, "yourAddress")}
                      label={
                        <span className="labelWrapper">
                          <span className="radioLabels">
                            <strong>{t("YOUR_ADDRESS")}</strong>
                            {temporaryBillingAddress?.line1 && <>{temporaryBillingAddress?.line1}</>}
                            {temporaryBillingAddress?.line2 && <>,&#160;{temporaryBillingAddress?.line2}</>}
                            {temporaryBillingAddress?.city && <><br />{temporaryBillingAddress?.city}</>}
                            {temporaryBillingAddress?.countrySubdivision && <>,&#160;{temporaryBillingAddress?.countrySubdivision}</>}
                            {temporaryBillingAddress?.postalCode && <>&#160;{temporaryBillingAddress?.postalCode}</>}
                            {temporaryBillingAddress?.countryName && <><br />{temporaryBillingAddress?.countryName}</>}
                          </span>
                        </span>
                      }
                    />
                    {shouldShowBillingRecommended && (
                      <Form.Check
                        type="radio"
                        name="billingAddressOption"
                        id="recommendedBillingAddress"
                        checked={selectedBillingAddressOption === "recommendedAddress"}
                        onChange={(e) => onBillingAddressOptionChange?.(e, "recommendedAddress")}
                        label={
                          <span className="labelWrapper">
                            <span className="radioLabels">
                              <strong>{t("RECOMMENDED_ADDRESS")}</strong>
                              {recommendedBillingAddress.address && (
                                <span className={isDifferent('address', billingAddressDifferences) ? 'address-different' : ''}>
                                  {recommendedBillingAddress.address}
                                </span>
                              )}
                              {recommendedBillingAddress.line2 && (
                                <span className={isDifferent('line2', billingAddressDifferences) ? 'address-different' : ''}>
                                  ,&#160;{recommendedBillingAddress.line2}
                                </span>
                              )}
                              {recommendedBillingAddress.cityValue && (
                                <><br /><span className={isDifferent('cityValue', billingAddressDifferences) ? 'address-different' : ''}>
                                  {recommendedBillingAddress.cityValue}
                                </span></>
                              )}
                              {recommendedBillingAddress.stateValue && (
                                <span className={isDifferent('stateValue', billingAddressDifferences) ? 'address-different' : ''}>
                                  ,&#160;{recommendedBillingAddress.stateValue}
                                </span>
                              )}
                              {recommendedBillingAddress.pincode && (
                                <span className={isDifferent('pincode', billingAddressDifferences) ? 'address-different' : ''}>
                                  &#160;{recommendedBillingAddress.pincode}
                                </span>
                              )}
                              {recommendedBillingAddress.countryName && (
                                <><br /><span className={isDifferent('countryName', billingAddressDifferences) ? 'address-different' : ''}>
                                  {recommendedBillingAddress.countryName}
                                </span></>
                              )}
                            </span>
                          </span>
                        }
                      />
                    )}
                  </div>
                </fieldset>
              </div>
            </div>
          )}

          {/* Shipping Address Section (or single address when isBilling is false) */}
          <div className={`address-column ${isBilling ? 'shipping-column' : ''}`}>
            {isBilling && <h5 className="address-section-title">{t("SHIPPING_ADDRESS")}</h5>}
            <div className="address-options">
              <fieldset aria-label={t("ADDRESS_OPTIONS")}>
                <legend className="visually-hidden">{t("ADDRESS_OPTIONS")}</legend>
                <div className="radioGroup">
                  <Form.Check
                    type="radio"
                    name="addressOption"
                    id="yourAddress"
                    checked={selectedAddressOption === "yourAddress"}
                    onChange={(e) => onAddressOptionChange(e, "yourAddress")}
                    label={
                      <span className="labelWrapper">
                        <span className="radioLabels">
                          <strong>{t("YOUR_ADDRESS")}</strong>
                          {temporaryAddress?.line1 && <>{temporaryAddress?.line1}</>}
                          {temporaryAddress?.line2 && <>,&#160;{temporaryAddress?.line2}</>}
                          {temporaryAddress?.city && <><br />{temporaryAddress?.city}</>}
                          {temporaryAddress?.countrySubdivision && <>,&#160;{temporaryAddress?.countrySubdivision}</>}
                          {temporaryAddress?.postalCode && <>&#160;{temporaryAddress?.postalCode}</>}
                          {temporaryAddress?.countryName && <><br />{temporaryAddress?.countryName}</>}
                        </span>
                      </span>
                    }
                  />
                  {shouldShowShippingRecommended && (
                    <Form.Check
                      type="radio"
                      name="addressOption"
                      id="recommendedAddress"
                      checked={selectedAddressOption === "recommendedAddress"}
                      onChange={(e) =>
                        onAddressOptionChange(e, "recommendedAddress")
                      }
                      label={
                        <span className="labelWrapper">
                          <span className="radioLabels">
                            <strong>{t("RECOMMENDED_ADDRESS")}</strong>
                            {recommendedShippingAddress.address && (
                              <span className={isDifferent('address', shippingAddressDifferences) ? 'address-different' : ''}>
                                {recommendedShippingAddress.address}
                              </span>
                            )}
                            {recommendedShippingAddress.line2 && (
                              <span className={isDifferent('line2', shippingAddressDifferences) ? 'address-different' : ''}>
                                ,&#160;{recommendedShippingAddress.line2}
                              </span>
                            )}
                            {recommendedShippingAddress.cityValue && (
                              <><br /><span className={isDifferent('cityValue', shippingAddressDifferences) ? 'address-different' : ''}>
                                {recommendedShippingAddress.cityValue}
                              </span></>
                            )}
                            {recommendedShippingAddress.stateValue && (
                              <span className={isDifferent('stateValue', shippingAddressDifferences) ? 'address-different' : ''}>
                                ,&#160;{recommendedShippingAddress.stateValue}
                              </span>
                            )}
                            {recommendedShippingAddress.pincode && (
                              <span className={isDifferent('pincode', shippingAddressDifferences) ? 'address-different' : ''}>
                                &#160;{recommendedShippingAddress.pincode}
                              </span>
                            )}
                            {recommendedShippingAddress.countryName && (
                              <><br /><span className={isDifferent('countryName', shippingAddressDifferences) ? 'address-different' : ''}>
                                {recommendedShippingAddress.countryName}
                              </span></>
                            )}
                          </span>
                        </span>
                      }
                    />
                  )}
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        
        {/* Display consolidated validation errors */}
        {isBilling ? (
          // Dual address mode: show errors for both
          <>
            {renderAddressErrors(billingValidationResult, formBillingAddress, t("BILLING_ADDRESS"))}
            {renderAddressErrors(shippingValidationResult, formShippingAddress, t("SHIPPING_ADDRESS"))}
            {/* Partner US state validation errors (e.g. MSI, iBUYPOWER) */}
            {['MSI', 'IBUYPOWER'].includes((brand || '').trim().toUpperCase()) && validationErrors?.billingaddress && (
              <div className="text-danger mt-2" role="alert" style={{ padding: '0.75rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                <strong>{t("BILLING_ADDRESS")}:</strong> {validationErrors.billingaddress}
              </div>
            )}
            {['MSI', 'IBUYPOWER'].includes((brand || '').trim().toUpperCase()) && validationErrors?.shippingaddress && (
              <div className="text-danger mt-2" role="alert" style={{ padding: '0.75rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                <strong>{t("SHIPPING_ADDRESS")}:</strong> {validationErrors.shippingaddress}
              </div>
            )}
          </>
        ) : (
          // Single address mode: show error for billing only
            <>
              {renderAddressErrors(billingValidationResult, formBillingAddress, "")}
              {/* Partner state validation errors (shipping only) */}
              {['MSI', 'IBUYPOWER'].includes((brand || '').trim().toUpperCase()) && validationErrors?.shippingaddress && (
                <div className="text-danger mt-2" role="alert" style={{ padding: '0.75rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                  {validationErrors.shippingaddress}
                </div>
              )}
            </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <h2 aria-label={t('NOTE_TEXT')}>
          {t('NOTE_TEXT')}
        </h2>
        <ul>
          <li aria-label={t('NOTE_1')}>
            <span>{t('NOTE_1')}</span>
          </li>
         
            <li aria-label={t('NOTE_2')}>
              <span dangerouslySetInnerHTML={{ __html: t('NOTE_2') }} />
            </li>
          
          {locale === 'en-gb' && <li aria-label={t('NOTE_3')}>
            <span>
              {t('NOTE_3')}
            </span>
          </li>}
        </ul>
        <div className="button-container">
          <Button
            variant="success-outline"
            className="btn-green-outline"
            onClick={onShippingAddressUpdate}
            aria-label={t('UPDATE_ADDRESS_BUTTON')}
          >
            {t('UPDATE_ADDRESS_BUTTON')}
          </Button>
          {canConfirmAddress && (
            <Button
              variant="success"
              className="btn-green"
              onClick={(e) => onConfirmAddress(e)}
              aria-label={t('CONFIRM_ADDRESS_BUTTON')}
              autoFocus
            >
              {t('CONFIRM_ADDRESS_BUTTON')}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddressModal;
